/**
 * units.ts — v2.3.2 UNIT-AWARE I/O (dispatch web)
 *
 * RULE: every distance/speed in the database is METRIC and canonical
 *       (metres, metres/second). The unit toggle is a *presentation + entry*
 *       layer only:
 *
 *          DB (metric)  --toDisplay-->  input box (ft / mi / mph)
 *          input box    --toMetric--->  DB (metric)
 *
 * So Lyndon can type "16 ft" for target spacing and 4.877 m is what is stored;
 * flip to metric and the same setting reads back as 4.9 m. Nothing drifts,
 * because we never round-trip through the display value — the metric number is
 * the source of truth and is only re-rendered.
 *
 * The dispatcher's choice lives in localStorage (dispatch_units_distance /
 * dispatch_units_small), shared with MapPage, and is seeded from the fleet
 * default (system_settings gps_map_units_*) the first time.
 */
export type DistUnit = 'mi' | 'km';
export type SmallUnit = 'ft' | 'm';

export const M_PER_FT = 0.3048;
export const M_PER_MI = 1609.344;
export const MPH_PER_MPS = 2.2369362920544;
export const KMH_PER_MPS = 3.6;

export const LS_DIST = 'dispatch_units_distance';
export const LS_SMALL = 'dispatch_units_small';
export const LS_UNCERT = 'dispatch_uncertainty';

export function getUnits(): { distance: DistUnit; small: SmallUnit } {
  const d = (localStorage.getItem(LS_DIST) as DistUnit) || 'mi';
  const s = (localStorage.getItem(LS_SMALL) as SmallUnit) || 'm';
  return { distance: d === 'km' ? 'km' : 'mi', small: s === 'ft' ? 'ft' : 'm' };
}
export function setUnits(u: Partial<{ distance: DistUnit; small: SmallUnit }>) {
  if (u.distance) localStorage.setItem(LS_DIST, u.distance);
  if (u.small) localStorage.setItem(LS_SMALL, u.small);
  window.dispatchEvent(new Event('dispatch-units-changed'));
}

/** What a field is measured in, IN THE DATABASE. */
export type UnitKind =
  | 'len_m'      // stored metres  -> shown/entered in ft or m
  | 'dist_m'     // stored metres, long haul -> shown/entered in mi or km
  | 'speed_mps'  // stored m/s     -> shown/entered in mph or km/h
  | 'speed_mph'  // stored mph (legacy key) -> shown/entered in mph or km/h
  | 'len_ft'     // stored feet (legacy key) -> shown/entered in ft or m
  | 'sec' | 'ms' | 'count' | 'deg' | 'ratio' | 'none';

/** Suffix shown on the input label / read-out for the ACTIVE unit system. */
export function unitLabel(kind: UnitKind, u = getUnits()): string {
  switch (kind) {
    case 'len_m':
    case 'len_ft':    return u.small;                            // ft | m
    case 'dist_m':    return u.distance;                         // mi | km
    case 'speed_mps':
    case 'speed_mph': return u.distance === 'km' ? 'km/h' : 'mph';
    case 'sec':       return 's';
    case 'ms':        return 'ms';
    case 'deg':       return '°';
    case 'ratio':     return 'x';
    default:          return '';
  }
}

/** DB value -> number to show in the box (in the active unit). */
export function toDisplay(v: number | string | null | undefined, kind: UnitKind, u = getUnits()): number | '' {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (!isFinite(n)) return '';
  switch (kind) {
    case 'len_m':    return u.small === 'ft' ? n / M_PER_FT : n;
    case 'len_ft':   return u.small === 'ft' ? n : n * M_PER_FT;
    case 'dist_m':   return u.distance === 'km' ? n / 1000 : n / M_PER_MI;
    case 'speed_mps':return n * (u.distance === 'km' ? KMH_PER_MPS : MPH_PER_MPS);
    case 'speed_mph':return u.distance === 'km' ? n * 1.609344 : n;
    default:         return n;
  }
}

/** Number typed in the box (active unit) -> canonical DB value. */
export function toMetric(v: number | string | null | undefined, kind: UnitKind, u = getUnits()): number | '' {
  if (v === '' || v === null || v === undefined) return '';
  const n = Number(v);
  if (!isFinite(n)) return '';
  switch (kind) {
    case 'len_m':    return u.small === 'ft' ? n * M_PER_FT : n;
    case 'len_ft':   return u.small === 'ft' ? n : n / M_PER_FT;
    case 'dist_m':   return u.distance === 'km' ? n * 1000 : n * M_PER_MI;
    case 'speed_mps':return n / (u.distance === 'km' ? KMH_PER_MPS : MPH_PER_MPS);
    case 'speed_mph':return u.distance === 'km' ? n / 1.609344 : n;
    default:         return n;
  }
}

/** Rounding used only for what the INPUT BOX shows — never for what we store. */
export function roundForInput(n: number | '', kind: UnitKind): number | '' {
  if (n === '') return '';
  const dp = kind === 'dist_m' ? 3 : kind === 'ratio' ? 2 : 2;
  const r = Number(n.toFixed(dp));
  return Object.is(r, -0) ? 0 : r;
}

/** Bounds are stored metric too — convert them for the box's min/max + helper text. */
export function boundsForDisplay(min: number | undefined, max: number | undefined, kind: UnitKind, u = getUnits()) {
  const c = (x?: number) => (x === undefined || x === null ? undefined : Number(toDisplay(x, kind, u)));
  return { min: c(min), max: c(max) };
}

// ---------- read-only formatters (displays, popups, stats) ----------
export const fmtLen = (m?: number | null, dp = 1, u = getUnits()) =>
  m == null || !isFinite(Number(m)) ? '—'
    : u.small === 'ft' ? `${(Number(m) / M_PER_FT).toFixed(dp)} ft` : `${Number(m).toFixed(dp)} m`;
export const fmtDistM = (m?: number | null, dp = 2, u = getUnits()) =>
  m == null || !isFinite(Number(m)) ? '—'
    : u.distance === 'km' ? `${(Number(m) / 1000).toFixed(dp)} km` : `${(Number(m) / M_PER_MI).toFixed(dp)} mi`;
export const fmtSpeedMps = (mps?: number | null, dp = 0, u = getUnits()) =>
  mps == null || !isFinite(Number(mps)) ? '—'
    : u.distance === 'km' ? `${(Number(mps) * KMH_PER_MPS).toFixed(dp)} km/h` : `${(Number(mps) * MPH_PER_MPS).toFixed(dp)} mph`;
